// src/components/auth/AuthContext.ts

import { createContext, useContext } from "react";
import type { User } from "@/types/auth.types";

/**
 * Auth context type - provider-agnostic interface
 *
 * This interface no longer depends on Auth0-specific types.
 * It works with any auth provider (Auth0, Supabase, etc.)
 */
export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  user: User | null; // Changed from Auth0 User to generic User
  loginWithRedirect: () => Promise<void>;
  logout: () => void;
  getAccessTokenSilently: () => Promise<string>;
  refreshUser: () => Promise<User | null>;
  updateUserData: (userData: Partial<User>) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

/**
 * Hook to access auth context
 * Use this in components instead of directly accessing AuthContext
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
